/**
 * How device drafts read on screen (ticket 12; the five states are ticket 33).
 * Wording lives here so the record screen's list and the home notice say the
 * same thing the same way.
 */
import { currentLang, storedErrorText, t } from './i18n';
import { MAX_AUDIO_BYTES, MAX_TEXT_CHARS, needsHearing, type BlockedReason, type Draft, type DraftKind, type DraftUiState, type DraftsView } from './drafts';
import { session } from './session';
import type { Allowance } from './types';

/** The learner's own timezone, when the allowance says (beta only). */
export function tzOf(a?: Allowance): string | undefined {
  return a && a.limit !== null ? a.timezone : undefined;
}

/** The lift time of a daily limit, in the learner's own timezone when known. */
export function resetTime(iso: string | undefined, timeZone?: string): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString(currentLang(), { hour: '2-digit', minute: '2-digit', ...(timeZone ? { timeZone } : {}) });
  } catch {
    return new Date(iso).toLocaleTimeString(currentLang());
  }
}

/** The day a monthly allowance comes back (same calendar as the AI lines elsewhere). */
export function resetDay(iso: string | undefined): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(currentLang(), { month: 'long', day: 'numeric', timeZone: 'UTC' });
}

/** Why a draft is waiting and when it goes. */
export function blockedLine(reason: BlockedReason | undefined, resetsAt: string | undefined, timeZone?: string): string {
  switch (reason) {
    case 'daily-allowance': return t('drafts.stateDaily', { time: resetTime(resetsAt, timeZone), timezone: timeZone ?? t('gate.localTime') });
    case 'daily-review': return t('drafts.stateReview', { time: resetTime(resetsAt, timeZone), timezone: timeZone ?? t('gate.localTime') });
    case 'beta-budget': return t('drafts.stateAiBeta', { date: resetDay(resetsAt) });
    default: return t('drafts.stateAiOwn', { date: resetDay(resetsAt) });
  }
}

/** The "saved here" line under a capture that just became a waiting draft. */
export function savedLine(kind: DraftKind, reason: BlockedReason | undefined, resetsAt: string | undefined, timeZone?: string): string {
  switch (reason) {
    case undefined: return t('record.savedHere');
    case 'daily-review': return t(kind === 'text' ? 'gate.keptText' : 'gate.keptAudio');
    case 'daily-allowance': return t('drafts.savedDaily', { time: resetTime(resetsAt, timeZone), timezone: timeZone ?? t('gate.localTime') });
    case 'beta-budget': return t('drafts.savedAiBeta', { date: resetDay(resetsAt) });
    default: return t('drafts.savedAiOwn', { date: resetDay(resetsAt) });
  }
}

/** An unheard Listen recording resumes in the lantern rather than through draft retries. */
export function unheardSavedLine(reason: BlockedReason | undefined, resetsAt?: string, timeZone?: string): string {
  switch (reason) {
    case undefined: return t('listen.unheard.kept');
    case 'daily-review': return t('listen.unheard.reviewKept');
    case 'daily-allowance': return t('listen.unheard.dailyKept', { time: resetTime(resetsAt, timeZone), timezone: timeZone ?? t('gate.localTime') });
    case 'beta-budget': return t('listen.unheard.betaKept', { date: resetDay(resetsAt) });
    default: return t('listen.unheard.aiKept', { date: resetDay(resetsAt) });
  }
}

/** One draft's lifecycle, as a sentence. */
export function stateLine(d: Draft, sending: boolean, timeZone?: string): string {
  if (sending) return t('record.sending');
  if (d.state === 'blocked') return needsHearing(d)
    ? unheardSavedLine(d.blockedReason, d.resetsAt, timeZone)
    : blockedLine(d.blockedReason, d.resetsAt, timeZone);
  if (d.state === 'failed') {
    const why = d.failReason === 'draft-account'
      ? t('drafts.whyAccount')
      : d.failReason === 'draft-language'
        ? t('drafts.whyLanguage')
        : d.failReason === 'too-large'
          ? t('drafts.whyTooLarge')
          : t('drafts.whyRejected', { err: storedErrorText(d.lastError ?? '') });
    return t('drafts.stateFailed', { why });
  }
  // Local-only: on this device, nothing accepted yet. A draft whose answer was
  // lost knows it may be a lesson already; it says so rather than claiming not
  // to have been sent (the next run asks about its own id first).
  return d.ambiguous ? t(needsHearing(d) ? 'listen.unheard.uncertain' : 'drafts.stateUnclear') : needsHearing(d) ? t('drafts.recordingKept') : t('drafts.stateLocal');
}

/**
 * The five states the screen shows — `local`, `blocked`, `sending`, `failed`
 * and `completed` — also the `data-state` tests and styles read. Every stored
 * draft is one of the first four; a draft that became a lesson is `completed`,
 * and one that turned out to be a lesson already deleted is `removed`.
 */
export function stateOf(d: Draft, v: DraftsView): DraftUiState {
  return v.sendingId === d.id ? 'sending' : d.state;
}

/** A draft that became a lesson this session, or one that turned out to be deleted already. */
export function completedState(c: { removed?: boolean }): 'completed' | 'removed' {
  return c.removed ? 'removed' : 'completed';
}

/** The one action a row's state allows, beside the always-available Delete. */
export interface DraftAction {
  /** `send` is what a manual "Send now" runs; `check` is the same tap on a blocked row. */
  kind: 'send' | 'check' | 'open';
  label: string;
}

/**
 * What a row offers, named for what it does: sending a draft the device still
 * only holds, checking one the server has refused, or opening a lesson the
 * draft already became.
 */
export function actionFor(state: DraftUiState): DraftAction | null {
  switch (state) {
    case 'local': return { kind: 'send', label: t('drafts.sendNow') };
    case 'blocked': return { kind: 'check', label: t('drafts.checkNow') };
    case 'failed': return null;
    case 'sending': return null;
    default: return { kind: 'open', label: t('drafts.open') };
  }
}

/** Whether a row's state can be tried now at all (a `failed` draft cannot be sent as it stands). */
export function tryable(state: DraftUiState): boolean {
  return state === 'local' || state === 'blocked';
}

/** The one-time notice after drafts over 30 days old were removed from this device (beta). */
export function expiredLine(n: number): string {
  return n === 1 ? t('drafts.expiredOne') : t('drafts.expiredMany', { n });
}

export function limitsHint(v: DraftsView): string {
  // What sending needs is the same in both modes: this app open and online,
  // signed in as the account the draft belongs to. It says so rather than
  // promising a time nobody is there for.
  const limits = t('drafts.hint', { max: v.max, mb: Math.round(MAX_AUDIO_BYTES / (1024 * 1024)), chars: MAX_TEXT_CHARS.toLocaleString(currentLang()) });
  // Beta only: personal-mode drafts never expire.
  const s = session();
  const beta = s.status === 'known' && s.beta;
  return [limits, ...(beta ? [t('drafts.hintAccount'), t('drafts.hintAge')] : [])].join(' ');
}

export function fmtCreated(ms: number): string {
  return new Date(ms).toLocaleString(currentLang(), { weekday: 'short', hour: '2-digit', minute: '2-digit' });
}
